import { I80Element, define, h } from "../base.js";
import { field, forward } from "../field.js";

/**
 * <i80-file-input label="Logo" name="logo" accept="image/png" helper-text="Max 2 MB"></i80-file-input>
 *
 * A labelled <input type="file">: the native control, only styled, so the browser's file
 * picker and multipart form submission work as usual. An <input> written inside the tag is
 * used as is, which is how server-rendered forms plug in:
 *   <i80-file-input label="Logo" error="{{ form.logo.errors.0 }}">{{ form.logo }}</i80-file-input>
 */
export class I80FileInput extends I80Element {
  static get observedAttributes() {
    return ["label", "helper-text", "error", "invalid", "required", "optional", "disabled", "width"];
  }

  setup() {
    let input = this.part("control") ?? this.querySelector("input");
    if (!input) {
      input = h("input", { type: "file" });
      forward(this, input, ["accept", "capture"]);
    }
    input.setAttribute("data-i80-part", "control");
    this._input = input;
    this._render = field(this, input);
  }

  adopt() {
    // A streamed response (or a template engine) can append the server's own <input> after
    // the element was built: that one is the author's, so it replaces the one we created.
    const late = this.takeContent().find((n) => n.nodeType === Node.ELEMENT_NODE && n.localName === "input");
    if (!late) return;
    this._input.remove();
    late.setAttribute("data-i80-part", "control");
    this._input = late;
    this._render = field(this, late);
  }

  update() {
    const input = this._input;
    if (this.hasAttribute("required")) input.required = this.flag("required");
    if (this.hasAttribute("disabled")) input.disabled = this.flag("disabled");
    this._render();
    // The Ember base control has no invalid variant: the error message carries the state.
    input.className = "i80-form-file-input i80-typography-body-200";
    input.style.width = this.getAttribute("width") ?? "";
  }

  /** The chosen files (empty until the user picks something; a file input has no value to set). */
  get files() {
    return this._input?.files ?? null;
  }

  get value() {
    return this._input?.value ?? "";
  }

  focus(options) {
    this._input?.focus(options);
  }
}

define("i80-file-input", I80FileInput);
